"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleAlert, Flame, Gauge, Pipette, ShieldAlert, Sparkles, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
interface ProblemItem {
  id: string;
  title: string;
  description: string;
  urgency: string;
  urgencyVariant: "default" | "secondary" | "destructive" | "outline";
  link: string;
  iconName: "clog" | "leak" | "heater" | "toilet" | "pressure" | "sewer" | "burst";
}
const problemsList: ProblemItem[] = [{
  id: "drain-clogs",
  title: "Clogged Drains & Backups",
  description: "Slow-moving sinks, standing water in showers, or stubborn backups that conventional plungers cannot clear.",
  urgency: "High Priority",
  urgencyVariant: "secondary",
  link: "/drain-problems/",
  iconName: "clog"
}, {
  id: "water-leaks",
  title: "Hidden Water Leaks",
  description: "Damp drywall, warm spots on floors, unexplainable water meter spikes, or steady dripping behind walls.",
  urgency: "Urgent",
  urgencyVariant: "destructive",
  link: "/water-leak-problems/",
  iconName: "leak"
}, {
  id: "water-heater-issues",
  title: "Water Heater Malfunctions",
  description: "No hot water, rust-tinted discharge, rumbling sounds from the tank, or pooling water around the base.",
  urgency: "Same-Day",
  urgencyVariant: "default",
  link: "/water-heater-problems/",
  iconName: "heater"
}, {
  id: "toilet-problems",
  title: "Toilet Overflow & Running",
  description: "Continuous running tanks, weak flushes, phantom cycling, or overflowing bowls risking floor damage.",
  urgency: "Urgent",
  urgencyVariant: "destructive",
  link: "/toilet-problems/",
  iconName: "toilet"
}, {
  id: "low-water-pressure",
  title: "Low Water Pressure",
  description: "Sudden drops in shower pressure, sputtering faucets, or restricted supply across specific fixtures.",
  urgency: "Inspection Needed",
  urgencyVariant: "outline",
  link: "/water-pressure-problems/",
  iconName: "pressure"
}, {
  id: "sewer-line-issues",
  title: "Sewer Line Backups",
  description: "Gurgling drains, foul sewage odors around the yard, or multiple fixtures backing up at the same time.",
  urgency: "Emergency",
  urgencyVariant: "destructive",
  link: "/sewer-line-repair/",
  iconName: "sewer"
}, {
  id: "burst-pipes",
  title: "Burst & Cracked Pipes",
  description: "Sudden flooding, ruptured supply lines, joint failures, or noisy vibrating pipes under high stress.",
  urgency: "Immediate 24/7",
  urgencyVariant: "destructive",
  link: "/pipe-problems/",
  iconName: "burst"
}];
function ProblemIcon({
  type
}: {
  type: ProblemItem["iconName"];
}) {
  switch (type) {
    case "clog":
      return <Pipette className="size-6 text-primary" />;
    case "leak":
      return <CircleAlert className="size-6 text-primary" />;
    case "heater":
      return <Flame className="size-6 text-primary" />;
    case "toilet":
      return <Zap className="size-6 text-primary" />;
    case "pressure":
      return <Gauge className="size-6 text-primary" />;
    case "sewer":
      return <AlertTriangle className="size-6 text-primary" />;
    case "burst":
      return <ShieldAlert className="size-6 text-primary" />;
    default:
      return <Wrench className="size-6 text-primary" />;
  }
}
export default function RiverdaleProblems() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="riverdaleplumbingproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide uppercase">
            <Sparkles className="size-3.5 text-accent mr-1" />
            Riverdale Plumbing Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-default font-bold text-foreground tracking-tight mb-4">
            Common Riverdale Plumbing Problems
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            From sudden pipe bursts to silent slab leaks and failing water heaters, our local emergency team resolves these frequent Riverdale plumbing failures quickly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {problemsList.map((problem, i) => <div key={problem.id} data-index={i} className="flex">
              <motion.div className="w-full flex" whileHover={shouldReduceMotion ? {} : {
            y: -4
          }} transition={{
            duration: 0.2
          }}>
                <Card className="w-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="size-12 rounded-lg bg-secondary flex items-center justify-center border border-border">
                          <ProblemIcon type={problem.iconName} />
                        </div>
                        <Badge variant={problem.urgencyVariant}>
                          {problem.urgency}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                        {problem.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm font-default text-muted-foreground leading-normal">
                        {problem.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2">
                    <Button variant="outline" size="sm" className="w-full justify-between group" asChild>
                      <Link to={problem.link}>
                        <span>Diagnose & Fix</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}

          <div data-index={problemsList.length} className="flex">
            <Card className="w-full flex flex-col justify-between border border-primary/20 bg-primary/5 shadow-sm p-2">
              <CardHeader className="pb-4">
                <div className="size-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center mb-3">
                  <Wrench className="size-6" />
                </div>
                <CardTitle className="text-xl font-default font-semibold text-foreground">
                  Facing a Different Issue?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm font-default text-muted-foreground leading-normal">
                  Our licensed technicians handle full diagnostics, leak detection, fixture repairs, and 24/7 urgent service across Riverdale.
                </CardDescription>
              </CardContent>
              <CardFooter className="pt-2 flex flex-col gap-2">
                <Button variant="default" size="sm" className="w-full justify-between" asChild>
                  <Link to="/problems/">
                    <span>View All Problems</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>

        <div className="mt-12 p-6 rounded-xl bg-secondary/50 border border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h3 className="font-default font-semibold text-base text-foreground">
              Experiencing an active leak or sewer backup right now?
            </h3>
            <p className="font-default text-sm text-muted-foreground">
              Jonesboro Emergency Plumber provides prompt dispatch to Riverdale and surrounding neighborhoods.
            </p>
          </div>
          <Button variant="destructive" size="lg" className="shrink-0" asChild>
            <Link to="/emergency-plumbing-problems/">
              Emergency Dispatch
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}